import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"

/**
 * Hook for fetching sites belonging to a specific customer.
 * Used in the AssignAssetDialog for cascading selection (customer → site).
 *
 * When customerId is null, the query is skipped and an empty array is returned.
 *
 * @param customerId - The customer ID to fetch sites for, or null to skip
 * @returns Sites list with id and name, plus loading state
 */
export function useSitesForCustomer(customerId: string | null): {
  sites: Array<{ id: string; name: string }>
  loading: boolean
} {
  const { data, loading, previousData } = useQueryWithEndpoint<
    gqlTypes.GetCustomerSitesQuery,
    gqlTypes.GetCustomerSitesQueryVariables
  >(
    gqlTypes.GetCustomerSitesDocument,
    {
      variables: { id: customerId || "" },
      skip: !customerId,
      fetchPolicy: "cache-first",
    },
    "primary",
  )

  if (!customerId) {
    return { sites: [], loading: false }
  }

  // Use previousData while loading to prevent visual flicker
  const effectiveData = data ?? previousData

  const sites: Array<{ id: string; name: string }> =
    effectiveData?.customerSites?.data?.map((site) => ({
      id: site.id,
      name: site.name,
    })) ?? []

  return { sites, loading: loading && !effectiveData }
}
